import React, {
  RefObject,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useMedia,
  useSidePanel,
} from 'core/hooks';
import Table from 'core/components/VirtualTable';
import {
  getChargesColumns,
  getChargesPanelInfo,
} from 'pages/Dashboard/pages/Billing/Charges/columns';
import {
  ChargesRowData,
} from 'pages/Dashboard/pages/Billing/types';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import clsx from 'clsx';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import useStorageState from 'core/hooks/storage';
import {
  ChargeDetailsResponseDTO,
  ChargesResponseDTO,
} from 'dtos';
import {
  postApiChargeGetCharges,
  postApiChargeGetChargeDetailsByEncounterIds,
} from 'endpoints';
import {
  getChargeSubRows,
  normalizeCharges,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  buildChargesRequest,
} from 'pages/Dashboard/pages/Billing/api';
import {
  BillingViewProps,
} from 'pages/Dashboard/pages/Billing/Charges';
import RowDetailsProvider, {
  useChargeDetails,
} from 'pages/Dashboard/pages/Billing/Charges/RowDetailsProvider';

function getRowId(row: ChargesRowData) {
  return row.rowKey
    ?? row.visitNoteBillingCodeId
    ?? row.visitNoteBillingCustomServiceCodeId
    ?? row.claimId
    ?? row.encounterId
    ?? 0;
}

type Props = {
  tableInstanceRef?: RefObject<VirtualTableInstance>;
};

function Overview({ tableInstanceRef }: Props) {
  const { requestMedia } = useMedia();
  const { openPanel } = useSidePanel();
  const columns = useMemo(() => getChargesColumns((row, tab) => {
    openPanel('charges', getChargesPanelInfo(row, tab));
  }), [openPanel]);
  const { isFetched, data } = useContext(DataContext);
  const {
    detailsById,
    fetchingIds,
    invalidateDetails,
  } = useChargeDetails();

  const [defaultPagination, setDefaultPagination] = useStorageState<ReactTablePaginationState>('chargesPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  const pagination = tableInstanceRef?.current?.pagination ?? defaultPagination ?? {};

  useEffect(() => {
    setDefaultPagination(pagination);
  }, [pagination]);

  useEffect(() => {
    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    // to match with the filters
    const unsubscribeFromClaimTagsUpdated = billingEventChannel.on('onClaimTagsUpdated', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    const unsubscribeFromPaymentCollect = paymentsEventChannel.on('onPatientBalanceChange', () => {
      const { refetch } = tableInstanceRef?.current ?? {};
      refetch?.();
    });

    const unsubscribeFromClaimUpsert = billingEventChannel.on('onClaimUpsert', (encounterId) => {
      invalidateDetails([encounterId]);
    });

    return () => {
      unsubscribeFromChargesInfoChange();
      unsubscribeFromPaymentCollect();
      unsubscribeFromClaimTagsUpdated();
      unsubscribeFromClaimUpsert();
    };
  }, []);

  useEffect(() => {
    if (isFetched) {
      requestMedia((data ?? [])?.map(({ patient }) => patient?.patientId));
    }
  }, [isFetched, data]);

  const subRowsByEncounterId = useMemo(() => {
    const subRows = new Map<number, ReturnType<typeof getChargeSubRows>>();
    (data ?? []).forEach((row: ChargesRowData) => {
      const encounterId = row.encounterId ?? 0;
      subRows.set(
        encounterId,
        getChargeSubRows(
          row,
          detailsById[encounterId],
          fetchingIds.includes(encounterId),
        ),
      );
    });
    return subRows;
  }, [data, detailsById, fetchingIds]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col overflow-y-hidden charges-table-container">
      <div className="w-full h-full">
        <Table<ChargesRowData>
          instanceRef={tableInstanceRef}
          getSubRows={(row) => (
            row.isChild
              ? undefined
              : subRowsByEncounterId.get(row.encounterId ?? 0)
          )}
          getRowCanExpand={(row) => row.original?.expandable ?? false}
          skeleton={{ rows: 8 }}
          columns={columns}
          onRowClick={async (table, row) => {
            openPanel('charges', getChargesPanelInfo(row.original, 'charge'));
          }}
          getRowId={getRowId}
          classes={{
            header: '!py-0.5 px-4 !items-end',
            outerColumn: '!items-start',
            column: '!text-left',
            scrollContainer: 'justify-center items-center',
            outerRow: '!w-[calc(100%-16px)] left-4',
            row: '!items-start',
            cell: ({ original: { isClaim } }) => clsx('!items-start', {
              '!mt-0': isClaim,
            }),
            subRow: ({ original: { isClaim } }) => clsx('!items-end', {
              '!bg-gray-100 !py-1': isClaim,
              '!pb-2 !border-t !border-gray-200': !isClaim,
            }),
          }}
          loadingSlot="Loading Charges..."
        />
      </div>
    </div>
  );
}

export default function ChargesView({
  dateRange,
  searchQuery,
  filterQuery,
  tableInstanceRef,
}: BillingViewProps) {
  const [defaultPagination] = useStorageState<ReactTablePaginationState>('chargesPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  return (
    <DataProvider<ChargesResponseDTO, ChargesRowData>
      queryKey="charges"
      pagination={defaultPagination}
      getData={postApiChargeGetCharges}
      getMeta={(response) => (response?.charges?.pageProperties ?? {})}
      getQuery={({
        dateRange,
        searchQuery,
        filterQuery,
        pagination,
      }) => buildChargesRequest({
        dateRange,
        searchQuery,
        filterQuery,
        pagination,
      })}
      normalize={normalizeCharges}
      params={{
        dateRange,
        searchQuery,
        filterQuery,
      }}
    >
      <RowDetailsProvider<ChargeDetailsResponseDTO>
        queryKey="charge-details"
        fetchDetails={(ids) => postApiChargeGetChargeDetailsByEncounterIds({
          encounterIds: ids,
        })}
        getDetailsId={(details) => details.encounterId ?? 0}
      >
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
        />
      </RowDetailsProvider>
    </DataProvider>
  );
}
